/**
 * Config keys are internal service names (`SERVICE_NAME`). By default the same
 * value is passed to `sentry-cli` as `-p`. Set `sentryProjectSlug` when the
 * Sentry project slug differs (for example `payments` → `payments-web`).
 *
 * Sentry project names only allow lowercase letters, digits, and hyphens.
 * Renaming a project in Sentry generates a new DSN — update `sentryDsn` too.
 */
export type ProjectName =
  | 'admin'
  | 'agent'
  | 'ai'
  | 'browser-extension'
  | 'web'
  | 'mobile'
  | 'chat'
  | 'images'
  | 'graphql'
  | 'internal-slack'
  | 'scripts'
  | 'search'
  | 'webhooks'
  | 'partner-api'
  | 'cron'
  | 'emails'
  | 'slack'
  | 'sms'
  | 'test'
  | 'tripleseat-proxy'
  | 'tsm'
  | 'ai-functions'
  | 'venue-sync'
  | 'scheduled-event'
  | 'voice-agent'
  | 'leads-agent'
  | 'mcp'
  | 'venue-data-extraction'
  | 'batch-insert'
  | 'payments';

type MonitoringConfig = {
  org: string;
  projects: Record<
    ProjectName,
    {
      sentryDsn?: string;
      sentryProjectSlug?: string;
    }
  >;
};

export const resolveSentryProjectSlug = (project: ProjectName): string =>
  monitoringConfig.projects[project].sentryProjectSlug ?? project;

export const monitoringConfig: MonitoringConfig = {
  org: 'ande',
  projects: {
    admin: {
      sentryDsn:
        'https://da331c6c001b648ea18115d55d00ed7c@o4505638814941184.ingest.sentry.io/4505647189262336',
    },
    // Ande Agent: the turn worker, the attention tick and its cron monitor, the alarm forwarder.
    agent: {
      sentryDsn:
        'https://472f7544a4e6e32703085349bcd757c1@o4505638814941184.ingest.us.sentry.io/4512272501964800',
    },
    ai: {
      sentryDsn:
        'https://e4e02e28bb011a10a9bbfb7175703c7e@o4505638814941184.ingest.sentry.io/4506440145174528',
    },
    'browser-extension': {
      sentryDsn:
        'https://b77d3f9d12bd0b8b44bf3117d8e5c160@o4505638814941184.ingest.us.sentry.io/4510365236133888',
    },
    web: {
      sentryDsn:
        'https://f1363d6c6552d54909f91c65609111eb@o4505638814941184.ingest.sentry.io/4505647180218368',
    },
    mobile: {
      sentryDsn:
        'https://bfd5076d4f3b31c7de44d8b63e8673c2@o4505638814941184.ingest.sentry.io/4506419139313664',
    },
    chat: {},
    images: {
      sentryDsn:
        'https://28e39cd37bc8f972833c9d26393e8997@o4505638814941184.ingest.sentry.io/4505772692406272',
    },
    'internal-slack': {
      sentryDsn:
        'https://f46b76e5f803eead5e604a7dd5804f72@o4505638814941184.ingest.us.sentry.io/4510818611822592',
    },
    graphql: {
      sentryDsn:
        'https://f143c31a2cf64b55fb8c6b5c021f20c7@o4505638814941184.ingest.sentry.io/4505647199682560',
    },
    scripts: {
      sentryDsn:
        'https://b2a65e697da5d351fa3c2127116faa96@o4505638814941184.ingest.sentry.io/4505647205842944',
    },
    search: {
      sentryDsn:
        'https://c676e27b11ef5a877c91db90ea25844b@o4505638814941184.ingest.sentry.io/4505795406397440',
    },
    webhooks: {
      sentryDsn:
        'https://f38ad365a63ffef89207efe2a4e1386c@o4505638814941184.ingest.sentry.io/4505647203876864',
    },
    'partner-api': {
      sentryDsn:
        'https://4215eb636052d100b7e77f807aa5272a@o4505638814941184.ingest.us.sentry.io/4512323874717696',
    },
    cron: {
      sentryDsn:
        'https://4b29e44acbc5693dc1f356fb33d297fb@o4505638814941184.ingest.sentry.io/4506067640713216',
    },
    emails: {
      sentryDsn:
        'https://adc019ce3e92b12ceb91527dc0fd169c@o4505638814941184.ingest.us.sentry.io/4507914291183616',
    },
    slack: {
      sentryDsn:
        'https://0352e6ff77ce1aa6ca6d706138d66bb4@o4505638814941184.ingest.us.sentry.io/4508687990849536',
    },
    sms: {
      sentryDsn: undefined,
    },
    test: {},
    'tripleseat-proxy': {},
    tsm: {},
    'venue-sync': {
      sentryDsn:
        'https://630541b41492e5fcb24577b7ea385569@o4505638814941184.ingest.us.sentry.io/4509585436311552',
    },
    'ai-functions': {
      sentryDsn:
        'https://e5a1766faea023e5f0ff5ff688420308@o4505638814941184.ingest.us.sentry.io/4511196705849344',
    },
    'scheduled-event': {
      sentryDsn:
        'https://ed5bc7628c39e16fb1b9758648d8990b@o4505638814941184.ingest.us.sentry.io/4511134050615296',
    },
    mcp: {
      sentryDsn:
        'https://a3ec01f3ac7ebb777185e9bdbdf34365@o4505638814941184.ingest.us.sentry.io/4511094674882560',
    },
    'voice-agent': {
      sentryDsn:
        'https://d0e390bbd31a7bfaa9036582472cb5a0@o4505638814941184.ingest.us.sentry.io/4511972403052544',
    },
    'leads-agent': {
      sentryDsn:
        'https://20c407400a990497d6b9d739a44de543@o4505638814941184.ingest.us.sentry.io/4511745806041093',
    },
    'venue-data-extraction': {},
    'batch-insert': {},
    payments: {
      sentryProjectSlug: 'payments-web',
      sentryDsn:
        'https://4485be2d8ceeafc12028342cf088c84b@o4505638814941184.ingest.us.sentry.io/4512181962670080',
    },
  },
};

export const isValidProjectName = (candidate?: string | null): candidate is ProjectName =>
  !!candidate && candidate in monitoringConfig.projects;
